import { z } from 'zod'
import { timeOfDaySchema } from '../validators/time-of-day.validator.js'
import { wallClockSchema } from '../validators/temporal.validator.js'

/**
 * One row of a venue's timeline template: a time of day and what happens.
 *
 * Only a time and a description. See `docs/event-timeline.md` — a run sheet is
 * read at a glance, and an owner or a note is one more column to keep true
 * across a hundred events.
 */
export const venueTimelineEntryDtoSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  /** A time of day at the venue, like the venue's own default window. */
  startsAt: timeOfDaySchema,
  /**
   * The row's place in the running order — the authority, not its clock. A
   * template may cross midnight, and `00:30` sorts before `11:00` in every
   * string comparison ever written.
   */
  sortOrder: z.number().int().nonnegative(),
})

/**
 * A named shape of a day, kept on the venue — "Saturday wedding", "Weekday
 * corporate". One per venue is the default and seeds a new event the moment the
 * event has a date; any of them can be applied to an event afterwards.
 */
export const venueTimelineTemplateDtoSchema = z.object({
  id: z.string().uuid(),
  venueId: z.string().uuid(),
  name: z.string(),
  /** Exactly one template per venue carries this. It is what seeds new events. */
  isDefault: z.boolean(),
  entries: z.array(venueTimelineEntryDtoSchema),
})

export type VenueTimelineTemplateDto = z.infer<typeof venueTimelineTemplateDtoSchema>

export const venueTimelineTemplateListDtoSchema = z.object({
  templates: z.array(venueTimelineTemplateDtoSchema),
  total: z.number().int().nonnegative(),
})

export type VenueTimelineTemplateListDto = z.infer<typeof venueTimelineTemplateListDtoSchema>

/**
 * One moment in an event's day.
 *
 * `startsAt` is a wall-clock time on the event's date, stored and shown without
 * conversion — the convention `docs/event-scheduling.md` sets for every event
 * time. An entry after midnight carries the following day's date, the way a
 * window's `endsAt` already does.
 *
 * There is no end. An entry runs until the next one starts, and the last runs to
 * the event's own `endsAt`.
 */
export const eventTimelineEntryDtoSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  label: z.string(),
  startsAt: wallClockSchema,
  /** Settles two entries at the same minute; the clock decides everything else. */
  sortOrder: z.number().int().nonnegative(),
})

export type EventTimelineEntryDto = z.infer<typeof eventTimelineEntryDtoSchema>

export const eventTimelineDtoSchema = z.object({
  entries: z.array(eventTimelineEntryDtoSchema),
  total: z.number().int().nonnegative(),
})

export type EventTimelineDto = z.infer<typeof eventTimelineDtoSchema>
